import React from 'react';

const ProductJSONLD = ({ 
  productData, 
  templateType, 
  pageSlug, 
  dataSource, 
  variantName 
}) => {
  const getCurrentPrice = () => {
    if (!productData?.item_data) return productData?.price || 0;
    
    const now = new Date();
    const earlyBirdDate = productData.item_data.early_bird?.date ? new Date(productData.item_data.early_bird.date) : null;
    const isEarlyBirdActive = earlyBirdDate && now <= earlyBirdDate && productData.item_data.early_bird.price > 0;
    
    if (isEarlyBirdActive) {
      return productData.item_data.early_bird.price;
    }
    return productData?.price || 0;
  };

  const getVariantSuffix = () => {
    const versionNumber = templateType.slice(-1);
    const variantSuffix = variantName ? ` (${variantName})` : '';
    return `-${versionNumber}${variantSuffix}`;
  };

  const getPrimaryCategory = () => {
    const categories = productData?.item_data?.categories || [];
    return categories.length > 0 ? categories[0].title || categories[0] : 'Business Course';
  };

  const getInstructor = () => {
    const instructors = productData?.item_data?.instructors || [];
    return instructors.length > 0 ? instructors[0] : null;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${productData?.title || 'FounderPlus Course'}${getVariantSuffix()}`,
    "description": productData?.item_data?.bootcamp_kind || `${dataSource} Course - Practical business training for founders`,
    "brand": {
      "@type": "Brand",
      "name": "FounderPlus"
    },
    "category": getPrimaryCategory(),
    "sku": `${productData?.uuid || pageSlug}${getVariantSuffix()}`,
    "productID": productData?.uuid || pageSlug,
    "url": `${window.location.origin}/landing/${pageSlug}`,
    "image": getInstructor()?.photo || `${window.location.origin}/images/founderplus-course.jpg`,
    "offers": {
      "@type": "Offer",
      "price": getCurrentPrice(),
      "priceCurrency": "IDR",
      "availability": productData?.item_data?.quota_status === 'available' ? "https://schema.org/InStock" : "https://schema.org/LimitedAvailability",
      "validFrom": new Date().toISOString(),
      "seller": {
        "@type": "Organization",
        "name": process.env.REACT_APP_NAME || "FounderPlus Academy",
        "url": window.location.origin
      },
      "priceValidUntil": productData?.item_data?.early_bird?.date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Person",
          "name": "Verified Customer"
        },
        "reviewBody": "Excellent course with practical insights for business management."
      }
    ],
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Course Type",
        "value": productData?.item_data?.event_type || "Online"
      },
      {
        "@type": "PropertyValue", 
        "name": "Sessions Count",
        "value": productData?.item_data?.sessions_count || 2
      },
      {
        "@type": "PropertyValue",
        "name": "Duration",
        "value": productData?.item_data?.dates || "2 Days"
      },
      {
        "@type": "PropertyValue",
        "name": "Data Source",
        "value": dataSource
      },
      {
        "@type": "PropertyValue",
        "name": "Layout Version",
        "value": templateType
      },
      {
        "@type": "PropertyValue",
        "name": "Page Variant",
        "value": variantName || "Default"
      },
      // Add subscription-specific properties if available
      ...(productData?.item_data?.duration ? [{
        "@type": "PropertyValue",
        "name": "Subscription Duration (Months)",
        "value": productData.item_data.duration
      }] : []),
      ...(productData?.item_data?.is_pinned !== undefined ? [{
        "@type": "PropertyValue",
        "name": "Is Featured Package",
        "value": productData.item_data.is_pinned
      }] : []),
      ...(productData?.item_data?.package_position ? [{
        "@type": "PropertyValue",
        "name": "Package Position",
        "value": productData.item_data.package_position
      }] : []),
      // Add learning program specific properties
      ...(productData?.item_data?.program_slug ? [{
        "@type": "PropertyValue",
        "name": "Program Slug",
        "value": productData.item_data.program_slug
      }] : []),
      ...(productData?.item_data?.program_uuid ? [{
        "@type": "PropertyValue",
        "name": "Program UUID",
        "value": productData.item_data.program_uuid
      }] : [])
    ]
  };

  const instructor = getInstructor();
  if (instructor) {
    structuredData.instructor = {
      "@type": "Person",
      "name": instructor.name,
      "jobTitle": instructor.title,
      "description": instructor.bio
    };
  }

  if (productData?.original_price && productData.original_price !== getCurrentPrice()) {
    structuredData.offers.priceSpecification = {
      "@type": "PriceSpecification",
      "price": getCurrentPrice(),
      "priceCurrency": "IDR",
      "referencePrice": {
        "@type": "PriceSpecification", 
        "price": productData.original_price,
        "priceCurrency": "IDR"
      }
    };
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData, null, 2)
      }}
    />
  );
};

export default ProductJSONLD;